import { useNavigate, useParams } from "react-router-dom";
import { useState, useRef, useEffect, useMemo } from "react";
import React from "react";
import "../Home/Home.css";
import "./CreationForm.css";
import { AdminHeader } from "../Header/AdminHeader";
import { AdminSidebar } from "../Sidebar/AdminSidebar";
import MultiSelect from "react-multiple-select-dropdown-lite";
import "react-multiple-select-dropdown-lite/dist/index.css";
import {
  checkUniqueUsername,
  getALLRolesList,
  getUserDetails,
  registerUser,
} from "_services/userCreationApi";
import SimpleTable from "_components/SimpleTable";
import RoleAssignTables from "./RoleAssignTables";
import toastError, { toastSuccess } from "_hooks/toast";
import LoadingOverlay from "_components/overlay/LoadingOverlay";
import { hasPermission, permissionConfig } from "_helpers/permissions";
export { UserCreation };

function UserCreation() {
  const [dropdownOptions, setDropDownOptions] = useState({});
  const [loader, setLoader] = useState(false);
  const [activeTab, setActiveTab] = useState("Tab1");
  const [activeRole, setActiveRole] = useState("vendors");
  const [lockedStatus, setLockedStatus] = useState(false);
  const [preSelectedOptions, setPreSelectedOptions] = useState({});
  const { userId } = useParams();
  const adminAccess = hasPermission(permissionConfig.adminAccess);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    userId: "",
    userName: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    active: true,
    isLocked: false,
    phoneCode: "",
    phoneNumber: "",
    userRole: "",
  });

  const [errors, setErrors] = useState({});
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setLoader(true);
      const apiResponse = await getALLRolesList();
      setDropDownOptions(apiResponse?.data);
      if (userId) {
        const userDetails = await getUserDetails(userId, adminAccess);
        if (userDetails?.success) {
          setFormData({
            userId: userDetails?.data.data.id || "",
            userName: userDetails?.data.data.username || "",
            firstName: userDetails?.data.data.firstName || "",
            middleName: userDetails?.data.data.middleName || "",
            lastName: userDetails?.data.data.lastName || "",
            email: userDetails?.data.data.email || "",
            active: userDetails?.data.data.active,
            isLocked: userDetails?.data.data.isLocked,
            phoneCode: userDetails?.data.data.countryCode || "",
            phoneNumber: userDetails?.data.data.mobileNumber || "",
            userRole: getUserRole(userDetails?.data.data.roleData.id),
          });
          setLockedStatus(userDetails?.data.data.isLocked)
          const selectedVal = transformData(userDetails?.data.data);
          setPreSelectedOptions(selectedVal);
        }
      }
      setLoader(false);
    };
    fetchDropdownOptions();
  }, []);

  function getUserRole(roleId) {
    switch (roleId) {
      case 1:
        return "admin";
      case 2:
        return "stores";
      case 3:
        return "vendors";
      case 4:
        return "departments";
      case 5:
        return "buyers";
      case 6:
        return "subAdmin";
      default:
        return "unknown";
    }
  }

  function getRoleId(roleName) {
    switch (roleName) {
      case "admin":
        return 1;
      case "stores":
        return 2;
      case "vendors":
        return 3;
      case "departments":
        return 4;
      case "buyers":
        return 5;
      case "subAdmin":
        return 6;
      default:
        return -1;
    }
  }

  const transformData = (userdata) => {
    return {
      stores: userdata.userStores.map((store) => ({
        [store.store_code]: store.store_name,
      })),
      vendors: userdata.userSuppliers.map((supplier) => ({
        [supplier.supplier_code]: supplier.supplier_name,
      })),
      buyers: userdata.userBuyers.map((buyer) => ({
        [buyer.buyer_code]: buyer.buyer_name,
      })),
      departments: userdata.userDepartments.map((department) => ({
        [department.department_code]: department.department_name,
      })),
    };
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };
  const handleUserNameInputChange = async (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateUserName = async () => {
    if (formData.userName && formData.userName.length > 4) {
      setLoading(true);
      if (userId) {
        const response = await checkUniqueUsername({
          username: formData.userName,
          userId: userId,
        });
        setLoading(false);
        if (response?.success && !response.data.code) {
          setErrors((prev) => ({
            ...prev,
            userName: "User Name already exists!",
          }));
          return false;
        } else {
          setErrors((prev) => ({
            ...prev,
            userName: "",
          }));
          return true;
        }
      } else {
        const response = await checkUniqueUsername({
          username: formData.userName,
        });
        setLoading(false);
        if (response?.success && !response.data.code) {
          setErrors((prev) => ({
            ...prev,
            userName: "User Name already exists!",
          }));
          return false;
        } else {
          setErrors((prev) => ({
            ...prev,
            userName: "",
          }));
          return true;
        }
      }
    } else {
      setErrors((prev) => ({
        ...prev,
        userName: "User Name must be at least 5 characters long!",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.userName) newErrors.userName = "User Name is required";
    if (!formData.firstName) newErrors.firstName = "First Name is required";
    if (!formData.lastName) newErrors.lastName = "Last Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.userRole) newErrors.userRole = "User Role is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if ((await validateUserName()) && validateForm()) {
      setActiveRole(formData.userRole);
      if (formData.userRole === "subAdmin") {
        setActiveRole("vendors");
      }
      setActiveTab("Tab2");
    }
  };

  const handleSelectionChange = (updatedSelection) => {
    setPreSelectedOptions((prev) => ({
      ...prev,
      [activeRole]: updatedSelection,
    }));
  };

  const handleRegisterUser = async () => {
    if (
      preSelectedOptions[formData.userRole]?.length > 0 ||
      formData.userRole === "subAdmin"
    ) {
      setLoader(true);
      const payload = Object.entries({
        id: formData?.userId,
        email: formData.email,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        username: formData.userName,
        countryCode: formData.phoneCode,
        mobileNumber: formData.phoneNumber,
        role: getRoleId(formData.userRole),
        active: formData.active,
        unlockUser: !formData.isLocked,
        storeCodes:
          preSelectedOptions["stores"]?.map((obj) => Object.keys(obj)[0]) || [],
        supplierCodes:
          preSelectedOptions["vendors"]?.map((obj) => Object.keys(obj)[0]) ||
          [],
        departmentCodes:
          preSelectedOptions["departments"]?.map(
            (obj) => Object.keys(obj)[0]
          ) || [],
        buyerCodes:
          preSelectedOptions["buyers"]?.map((obj) => Object.keys(obj)[0]) || [],
      }).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {});
      const resp = await registerUser(payload);
      if (resp?.success) {
        toastSuccess(
          userId ? "✅ Sucessfully updated!" : "✅ Sucessfully created!"
        );
        navigate("/admin-user-list");
      }
      setLoader(false);
    } else {
      toastError("Please add atleast one " + formData.userRole);
    }
  };

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    setTypingTimeout(
      setTimeout(() => {
        validateUserName();
      }, 1000)
    );
  }, [formData.userName]);


  return (
    <div className="admin-details-page-sec">
      <AdminSidebar />
      <div className="admin-details-page-body">
        <AdminHeader />
        <div className="user-creation-body relative d-flex flex-col">
          <div className="user-creation-title relative d-flex align-center justify-between">
            {activeTab === "Tab1" && "User Details"}
            {activeTab === "Tab2" && "Data Security"}
            <div className="tab-btns d-flex align-center">
              <button
                type="button"
                className={`btns tab-btn ${activeTab === "Tab1" ? "selected" : ""}`}
                onClick={() => setActiveTab("Tab1")}
              >
                {" "}
                User Details
              </button>
              <button
                type="button"
                className={`btns tab-btn ${activeTab === "Tab2" ? "selected" : ""}`}
                onClick={handleSubmit}
              >
                Data Security
              </button>
            </div>
          </div>
          <div className="user-creation-form-body relative d-flex flex-col justify-between">
            {activeTab === "Tab1" && (
              <div className="user-creation-box relative">
                <div className="user-creation-form-list relative d-flex">
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    <label className="m-0 p-0 relative">User Name  <span className="required">*</span></label>
                    <div className="input-container w-100">
                      <input
                        type="text"
                        className="form-control"
                        value={formData.userName}
                        onChange={(e) =>
                          handleUserNameInputChange("userName", e.target.value)
                        }
                      />
                      {loading && <div className="spinner2"></div>}
                    </div>
                    {errors.userName && (
                      <span className="error">{errors.userName}</span>
                    )}
                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    {userId && lockedStatus && (
                      <>
                        <label className="m-0 p-0 relative">Locked</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="statusSwitch"
                            checked={formData.isLocked === true}
                            onChange={(e) =>
                              handleInputChange(
                                "isLocked",
                                e.target.checked ? true : false
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="statusSwitch"
                          >
                            {formData.isLocked === true ? "Locked" : "Unlocked"}
                          </label>
                        </div>
                      </>
                    )}

                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    {userId && (
                      <>
                        <label className="m-0 p-0 relative">Status</label>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="statusSwitch"
                            checked={formData.active === true}
                            onChange={(e) =>
                              handleInputChange(
                                "active",
                                e.target.checked ? true : false
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="statusSwitch"
                          >
                            {formData.active === true ? "Active" : "Inactive"}
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    <label className="m-0 p-0 relative">First Name <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.firstName}
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                    />
                    {errors.firstName && (
                      <span className="error">{errors.firstName}</span>
                    )}
                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    <label className="m-0 p-0 relative">Middle Name (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.middleName}
                      onChange={(e) =>
                        handleInputChange("middleName", e.target.value)
                      }
                    />
                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    <label className="m-0 p-0 relative">Last Name <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.lastName}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                    />
                    {errors.lastName && (
                      <span className="error">{errors.lastName}</span>
                    )}
                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    <label className="m-0 p-0 relative">Email <span className="required">*</span></label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                    />
                    {errors.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    <label className="m-0 p-0 relative">Phone <span className="required">*</span></label>
                    <div className="country-code d-flex align-center w-100">
                      <select
                        className="form-control code"
                        value={formData.phoneCode}
                        onChange={(e) =>
                          handleInputChange("phoneCode", e.target.value)
                        }
                      >
                        {/* <option value="" disabled hidden>
                          Choose
                        </option> */}
                        <option value="+1">+1</option>
                        <option value="+52">+52</option>
                        <option value="+91">+91</option>
                        <option value="+63">+63</option>
                      </select>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.phoneNumber}
                        onChange={(e) =>
                          handleInputChange("phoneNumber", e.target.value)
                        }
                      />
                    </div>
                    {errors.phoneNumber && (
                      <span className="error">{errors.phoneNumber}</span>
                    )}
                  </div>
                  <div className="form-group m-0 p-0 relative d-flex flex-col align-start">
                    <label className="m-0 p-0 relative">User Role <span className="required">*</span></label>
                    <select
                      className="form-control"
                      value={formData.userRole}
                      onChange={(e) =>
                        handleInputChange("userRole", e.target.value)
                      }
                    >
                      <option value="" disabled hidden>
                        Choose
                      </option>
                      <option value="vendors">Vendor</option>
                      <option value="stores">Store Manager</option>
                      <option value="buyers">Buyer</option>
                      <option value="departments">Category Manager</option>
                      <option value="subAdmin">Sub admin</option>
                    </select>
                    {errors.userRole && (
                      <span className="error">{errors.userRole}</span>
                    )}
                  </div>
                </div>
                <div className="user-creation-button-list relative d-flex align-center justify-center">
                  <button
                    type="button"
                    className="btns user-creation-btn"
                    onClick={handleSubmit}
                  >
                    <span className="material-symbols-outlined">
                      arrow_forward
                    </span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === "Tab2" && (
              <div className="user-creation-box relative">
                <div className="user-creation-data-security">
                  <div className="tab-btns d-flex align-center mb-2">
                    <button
                      type="button"
                      className={`btns tab-btn ${activeRole === "vendors" ? "selected" : ""}`}
                      onClick={() => setActiveRole("vendors")}
                    >
                      Vendor{" "}
                    </button>
                    <button
                      type="button"
                      className={`btns tab-btn ${activeRole === "stores" ? "selected" : ""}`}
                      onClick={() => setActiveRole("stores")}
                    >
                      Store
                    </button>
                    <button
                      type="button"
                      className={`btns tab-btn ${activeRole === "departments" ? "selected" : ""}`}
                      onClick={() => setActiveRole("departments")}
                    >
                      Department
                    </button>
                    <button
                      type="button"
                      className={`btns tab-btn ${activeRole === "buyers" ? "selected" : ""}`}
                      onClick={() => setActiveRole("buyers")}
                    >
                      Buyers
                    </button>
                  </div>
                  {activeRole === "vendors" && (
                    <RoleAssignTables
                      dropdownOptions={dropdownOptions["vendors"]}
                      preSelectedOptions={preSelectedOptions["vendors"]}
                      onSelectionChange={handleSelectionChange}
                    />
                  )}
                  {activeRole === "stores" && (
                    <RoleAssignTables
                      dropdownOptions={dropdownOptions["stores"]}
                      preSelectedOptions={preSelectedOptions["stores"]}
                      onSelectionChange={handleSelectionChange}
                    />
                  )}
                  {activeRole === "departments" && (
                    <RoleAssignTables
                      dropdownOptions={dropdownOptions["departments"]}
                      preSelectedOptions={preSelectedOptions["departments"]}
                      onSelectionChange={handleSelectionChange}
                    />
                  )}
                  {activeRole === "buyers" && (
                    <RoleAssignTables
                      dropdownOptions={dropdownOptions["buyers"]}
                      preSelectedOptions={preSelectedOptions["buyers"]}
                      onSelectionChange={handleSelectionChange}
                    />
                  )}
                </div>
                <div className="user-creation-button-list relative d-flex align-center justify-center">
                  <button
                    type="button"
                    className="btns user-creation-btn cancel-btn"
                    onClick={() => setActiveTab("Tab1")}
                  >
                    <span className="material-symbols-outlined">
                      arrow_back
                    </span>
                  </button>
                  <button
                    type="button"
                    className="btns user-creation-btn"
                    onClick={handleRegisterUser}
                  >
                    <span className="material-symbols-outlined">save</span> Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {loader && <LoadingOverlay />}
    </div>
  );
}
